import { useEffect, useState } from "react"

import { api } from "~/utils/api"
import { isAfter, isToday } from "~/utils/datetime"

import LoadingOverlay from "../reusable/LoadingOverlay"
import { IngestionErrorsCard } from "./IngestionErrorsCard"
import { IngestionLogCard } from "./IngestionLogCard"
import { OrgIngestionOverview } from "./OrgIngestionOverview"
import { OrgTableMetaDataCard } from "./OrgTableMetaDataCard"
import OrgValidationReport from "./OrgValidationReport"
import { useAuth0 } from "@auth0/auth0-react"
import { useUser } from "~/providers/userProvider"

interface OrgIngestionTabProps {
  orgId: string
}

export const OrgIngestionsTab: React.FC<OrgIngestionTabProps> = ({ orgId }) => {
  const { user, isAuthenticated, isLoading } = useAuth0()
  const { permissions } = useUser()
  const [currentlyIngestingData, setCurrentlyIngestingData] = useState(false)

  const { data: updatedAt } = api.updatedAt.get.useQuery(undefined, {
    refetchInterval: currentlyIngestingData ? 5000 : 30000,
  })

  useEffect(() => {
    if (updatedAt && updatedAt.startTime) {
      setCurrentlyIngestingData(
        !updatedAt.endTime ||
          (isToday(updatedAt.startTime) && isAfter(updatedAt.startTime, updatedAt.endTime)),
      )
    } else {
      setTimeout(() => {
        // Make sure the components load the final state before stopping.
        setCurrentlyIngestingData(false)
      }, 10000)
    }
  }, [updatedAt])

  if (!user && isLoading) {
    return <LoadingOverlay />
  }

  if (!isAuthenticated) {
    return <div className="bg-subtle p-4">Not authenticated</div>
  }

  if (!permissions.isStaff) {
    return <div className="bg-subtle p-4">User not allowed!</div>
  }

  return (
    <div className="flex flex-col gap-2.5 min-w-full">
      <OrgIngestionOverview orgId={orgId} simple />
      <div className="grid grid-cols-2 gap-2.5 min-w-full">
        <div className="flex flex-col gap-2.5 h-full">
          <OrgValidationReport orgId={orgId} />
        </div>
        <div className="flex flex-col gap-2.5 h-full">
          <OrgTableMetaDataCard orgId={orgId} shouldRefresh={currentlyIngestingData} />
          <IngestionErrorsCard orgId={orgId} shouldRefresh={currentlyIngestingData} />
          <IngestionLogCard orgId={orgId} shouldRefresh={currentlyIngestingData} />
        </div>
      </div>
    </div>
  )
}
